import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Row } from "reactstrap";
import * as R from "ramda";
import { sap2Society, sap2Division, majorAccountSign } from "../../../../utils/sap-mapper";
import { accounts, cecosByAccountSocietyAndDivision } from "./cecos-configuration";

import SelectSection from "./select-section";
import NumericSection from "./numeric-section";

const BigAccounts = ({
  currency,
  showError,
  canEdit,
  isEditing,
  editingObject,
  sapCode,
  isValidChange,
  onValueChanged
}) => {
  const [customer, setCustomer] = useState(isEditing ? editingObject.customer : sapCode);
  const [society, setSociety] = useState(isEditing ? editingObject.society : "");
  const [amount, setAmount] = useState(isEditing ? editingObject.amount : 0);
  const [division, setDivision] = useState(isEditing ? editingObject.division : "");
  const [ceco, setCeco] = useState(isEditing ? editingObject.ceco : "");
  const observaciones = "";
  const [isValidAmount, setIsValidAmount] = useState(false);
  const isCecoRequired = customer && (customer.startsWith("6") || customer.startsWith("7"));
  const [societyConfig, setSocietyConfig] = useState([]);
  const [divisionConfig, setDivisionConfig] = useState([]);
  const [cecoConfig, setCecoConfig] = useState([]);
  useEffect(() => {
    const submitActive =
      !R.isEmpty(customer) &&
      (isCecoRequired ? (ceco !== -1 && ceco !== "-1") : true) &&
      society !== -1 &&
      society !== "-1" &&
      division !== -1 &&
      division !== "-1" &&
      isValidAmount;
    isValidChange(submitActive);
    // eliminamos la referencia a isValidChange para evitar infinitos renderizados
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, isCecoRequired, customer, division, society, isValidAmount, ceco]);
  useEffect(
    () =>
      onValueChanged({
        accountingKey: "",
        recArea: "",
        amount,
        customer,
        society,
        division,
        observaciones,
        ceco
      }),
    // eliminamos la referencia a onValueChanged para evitar infinitos renderizados
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [amount, customer, division, ceco, society, observaciones]
  );
  useEffect(() => {
    if (isEditing) {
      console.log("editing", editingObject);
      setCustomer(editingObject.customer);
      setSociety(editingObject.society);
      setDivision(editingObject.division);
      setCeco(editingObject.ceco);
    }
  }, [editingObject, isEditing]);

  useEffect(() => {
    if (customer && customer !== "-1" && cecosByAccountSocietyAndDivision[customer]) {
      const values = Object.keys(cecosByAccountSocietyAndDivision[customer] || []);
      setSocietyConfig(values);
      if (values.length === 1) {
        setSociety(values[0]);
      }
      setDivision(editingObject ? editingObject.division : "");
      setCeco(editingObject ? editingObject.ceco : "");
    }
  }, [customer, editingObject]);
  useEffect(() => {
    if (society && society !== "-1" && cecosByAccountSocietyAndDivision[customer]) {
      const values = Object.keys(cecosByAccountSocietyAndDivision[customer][society] || []);
      setDivisionConfig(values);
      if (values.length === 1) {
        setDivision(values[0]);
      }
      setCeco(editingObject ? editingObject.ceco : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [society]);

  useEffect(() => {
    if (
      division &&
      division !== "-1" &&
      cecosByAccountSocietyAndDivision[customer] &&
      cecosByAccountSocietyAndDivision[customer][society]
    ) {
      const values = cecosByAccountSocietyAndDivision[customer][society][division] || [];
      setCecoConfig(values);
      if (values.length === 1) {
        setCeco(values[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [division]);

  return (
    <div>
      <Row>
        <div className="col-12">
          <h4>Datos de cliente</h4>
        </div>
        <SelectSection
          required
          showError={showError}
          title="Cód SAP"
          onChange={newValue => setCustomer(newValue)}
          value={customer}
          options={accounts.map(entry => (
            <option value={entry.value} selected={customer === entry.value}>
              {entry.label}
            </option>
          ))}
          canEdit={canEdit}
        />
      </Row>
      {/* Item data */}
      <Row>
        <div className="col-12 mt-4">
          <h4>Datos de la partida</h4>
        </div>
        <SelectSection
          required={isCecoRequired}
          showError={showError}
          title="CECO"
          onChange={newValue => {
            setCeco(newValue);
          }}
          value={ceco}
          options={cecoConfig.map(entry => (
            <option value={entry} selected={ceco === entry}>
              {entry}
            </option>
          ))}
          canEdit={canEdit}
        />
        <NumericSection
          append={currency}
          showError={showError}
          title="Importe"
          negativePrepend={majorAccountSign(customer) < 0}
          required
          value={amount}
          onChange={newValue => {
            setAmount(newValue);
          }}
          canEdit={canEdit}
          isValidChange={newValue => setIsValidAmount(newValue)}
        />
        <SelectSection
          required
          showError={showError}
          title="Sociedad"
          onChange={newValue => {
            setSociety(newValue);
          }}
          value={society}
          options={Object.entries(sap2Society)
            .filter(x => societyConfig.includes(x[0]))
            .map(entry => (
              <option value={entry[0]} selected={society === entry[0]}>
                {`${entry[0]}: ${entry[1]}`}
              </option>
            ))}
          canEdit={canEdit}
        />
        <SelectSection
          required
          showError={showError}
          title="División"
          onChange={newValue => setDivision(newValue)}
          value={division}
          options={Object.entries(sap2Division)
            .filter(x => divisionConfig.includes(x[0]))
            .map(entry => (
              <option value={entry[0]} selected={division === entry[0]}>
                {`${entry[0]}: ${entry[1]}`}
              </option>
            ))}
          canEdit={canEdit}
        />
      </Row>
    </div>
  );
};
BigAccounts.propTypes = {
  currency: PropTypes.string,
  sapCode: PropTypes.string,

  isEditing: PropTypes.bool,
  editingObject: PropTypes.shape({
    amount: PropTypes.number,
    customer: PropTypes.string,
    society: PropTypes.string,
    division: PropTypes.string,
    ceco: PropTypes.string,
    observaciones: PropTypes.string
  }),
  canEdit: PropTypes.bool,
  isValidChange: PropTypes.func,
  onValueChanged: PropTypes.func,
  showError: PropTypes.bool
};

BigAccounts.defaultProps = {
  currency: "EUR",
  showError: false,
  sapCode: "",
  isEditing: false,
  editingObject: {
    observaciones: ""
  },
  canEdit: true,
  isValidChange: () => {},
  onValueChanged: () => {}
};
export default BigAccounts;
